import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, Loader2 } from "lucide-react";
import { paidAccessApi, type PaidResourceDetail } from "@/lib/api/paid-access";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/contexts/ToastContext";
import { cn } from "@/lib/utils";
import {
  paidResourceStateBadgeClass,
  paidResourceStateLabel,
} from "@/pages/patron/paid-resource-state";

export default function PaidResourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [resource, setResource] = useState<PaidResourceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    try {
      setResource(await paidAccessApi.getResource(id));
    } catch {
      setResource(null);
      setNotFound(true);
      toast.error("Resource not found or not available.");
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const onPurchase = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const result = await paidAccessApi.purchase(id);
      if (result.type === "free") {
        toast.success("Access granted.");
        await load();
      } else if (result.authorizationUrl) {
        window.location.href = result.authorizationUrl;
      }
    } catch {
      toast.error("Could not start payment.");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (!id || !resource) return;
    setBusy(true);
    try {
      await paidAccessApi.download(id, resource.title);
      toast.success("Download started.");
    } catch {
      toast.error("Download failed. Check your access or try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !resource) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Resource not found"
          description="This paid resource is unavailable or you do not have access."
          showSearch={false}
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Paid Resources", href: "/paid-resources" },
            { label: "Not found" },
          ]}
        />
        <Card className="border border-gray-200">
          <CardContent className="p-0">
            <EmptyState
              icon={<FileText className="h-6 w-6 text-slate-400" />}
              title="Resource unavailable"
              description="Return to Paid Resources to browse materials available for your account."
              action={
                <Button asChild variant="outline">
                  <Link to="/paid-resources">Browse paid resources</Link>
                </Button>
              }
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  const state = resource.entitlement.derivedState;
  const canDownload = state === "active";
  const needsPay = state !== "active";
  const priceLine =
    resource.price <= 0
      ? "Free for your patron type"
      : `${resource.currency} ${resource.price.toLocaleString()}`;
  const accessLine = resource.accessDurationDays
    ? `${resource.accessDurationDays} days access`
    : resource.price > 0
      ? "Permanent access"
      : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={resource.title}
        description={resource.description ?? "Paid digital resource"}
        showSearch={false}
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Paid Resources", href: "/paid-resources" },
          { label: resource.title },
        ]}
      />

      <Card className="border border-gray-200">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <CardTitle className="text-lg font-semibold text-gray-900">Access & pricing</CardTitle>
            <Badge variant="outline" className={cn("border", paidResourceStateBadgeClass(state))}>
              {paidResourceStateLabel(state)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-900">{priceLine}</span>
            {accessLine ? ` · ${accessLine}` : null}
          </p>

          {state === "revoked" && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Your previous access was revoked by the library. You can purchase again if pricing is
              still available.
            </p>
          )}
          {state === "expired" && (
            <p className="text-sm text-slate-600">
              Your access period has ended. Purchase again to download.
            </p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {needsPay && (
              <Button onClick={onPurchase} disabled={busy} className="gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {resource.price <= 0 ? "Get access" : "Pay to download"}
              </Button>
            )}
            {canDownload && (
              <Button variant="secondary" onClick={onDownload} disabled={busy} className="gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Download PDF
              </Button>
            )}
            {resource.entitlement.invoiceId && (
              <Button variant="outline" asChild>
                <Link to={`/invoices/${resource.entitlement.invoiceId}/receipt`}>View receipt</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
